/**
 * Cookie Consent Helper
 * Utilities for managing cookie consent state and interacting with Microsoft Clarity
 */

import {
  COOKIE_CONSENT_CONFIG,
  type CookieConsentState,
} from "../../../models/cookie-consent.model";

/**
 * Check if storage APIs are available using browser APIs
 * Uses navigator.cookieEnabled and checks for Storage API availability
 */
export const isStorageAvailable = (): boolean => {
  // Check if cookies are enabled (required for localStorage in many browsers)
  if (typeof navigator !== "undefined" && navigator.cookieEnabled === false) {
    return false;
  }

  // Check if Storage API exists
  if (typeof Storage === "undefined") {
    return false;
  }

  // Check if localStorage is accessible
  if (typeof localStorage === "undefined") {
    return false;
  }

  // Try to access localStorage (some browsers block access even if API exists)
  try {
    localStorage.length;
    return true;
  } catch {
    return false;
  }
};

/**
 * Get the current cookie consent state from localStorage
 */
export const getCookieConsent = (): CookieConsentState | null => {
  if (!isStorageAvailable()) {
    return null;
  }

  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_CONFIG.STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as CookieConsentState;
  } catch (error) {
    console.error("Error reading cookie consent from localStorage:", error);
    return null;
  }
};

/**
 * Save cookie consent state to localStorage
 */
export const setCookieConsent = (analyticsConsent: boolean): boolean => {
  if (!isStorageAvailable()) {
    console.warn("localStorage is not available, cannot save cookie consent");
    return false;
  }

  try {
    const consentState: CookieConsentState = {
      analyticsConsent,
      timestamp: new Date().toISOString(),
      hasResponded: true,
    };
    localStorage.setItem(COOKIE_CONSENT_CONFIG.STORAGE_KEY, JSON.stringify(consentState));
    return true;
  } catch (error) {
    console.error("Error saving cookie consent to localStorage:", error);
    return false;
  }
};

/**
 * Update Microsoft Clarity consent via Clarity Consent API v2
 * Reference: https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-consent-api-v2
 */
export const updateClarityConsent = (analyticsConsent: boolean): void => {
  try {
    // Check if Clarity is loaded
    if (typeof window.clarity === "function") {
      // Use Clarity Consent API v2
      // The consent method signature: clarity("consent", consentMode)
      // consentMode can be: "granted" or "denied"
      const consentMode = analyticsConsent
        ? COOKIE_CONSENT_CONFIG.ANALYTICS_CONSENT_GRANTED
        : COOKIE_CONSENT_CONFIG.ANALYTICS_CONSENT_DENIED;

      window.clarity("consent", consentMode);

      console.log(`Clarity consent updated: ${consentMode}`);
    } else {
      console.warn("Microsoft Clarity is not loaded yet");
    }
  } catch (error) {
    console.error("Error updating Clarity consent:", error);
  }
};

/**
 * Check if user has already responded to cookie consent
 */
export const hasUserRespondedToCookieConsent = (): boolean => {
  const consent = getCookieConsent();
  return consent?.hasResponded ?? false;
};

/**
 * Grant cookie consent (accept analytics)
 */
export const grantCookieConsent = (): void => {
  setCookieConsent(true);
  updateClarityConsent(true);
};

/**
 * Deny cookie consent (decline analytics)
 */
export const denyCookieConsent = (): void => {
  setCookieConsent(false);
  updateClarityConsent(false);
};

/**
 * Get current analytics consent status
 */
export const getAnalyticsConsentStatus = (): boolean => {
  const consent = getCookieConsent();
  return consent?.analyticsConsent ?? false;
};
